import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { Siren } from "lucide-react";
import { Process } from "@/components/home/Process";
import { CtaBand } from "@/components/site/CtaBand";
import { SERVICES, getService } from "@/data/services";
import { SITE } from "@/lib/site";
import NotFound from "./NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <NotFound />;
  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 3);
  return (
    <>
      <Seo title={`${s.title} Plumbing Sydney | Austyle Plumbing`} description={`${s.short} Austyle Plumbing – 30 years servicing Sydney.`} />
      <PageHero testid="service-hero" eyebrow={s.tag} lines={s.title.split(" ").length > 1 ? s.title.split(" ") : [s.title, "plumbing."]} image={s.image} pos={s.pos} intro={s.short} />
      <section className="bg-porcelain py-24 sm:py-32">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow text-cobalt mb-5">Overview</p></Reveal>
            <Reveal><p data-testid="service-intro" className="font-wide font-bold text-2xl sm:text-3xl leading-snug tracking-tight">{s.intro}</p></Reveal>
            <h2 className="mt-16 font-wide font-black uppercase text-3xl sm:text-4xl tracking-tight"><MaskLines lines={["What's included"]} /></h2>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {s.includes.map((x, i) => (
                <Reveal as="li" key={x} delay={i * 0.04} className="bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)] flex gap-3 p-5 text-slate-800">
                  <Check className="h-5 w-5 text-cobalt shrink-0" /> {x}
                </Reveal>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 bg-ink text-white p-8 sm:p-10 clip-notch">
              <p className="eyebrow text-brass">Book {s.title.toLowerCase()}</p>
              <p className="font-wide font-black text-3xl mt-4 leading-tight">Pick a time or get a quote in minutes.</p>
              <Link to={`/book?service=${encodeURIComponent(s.title)}`} data-testid="service-book-button" className="group mt-8 flex items-center justify-between bg-brass text-ink font-bold h-14 px-6 rounded-full hover:bg-white transition-colors">
                Book this service <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={SITE.tel} data-testid="service-call-button" className="mt-3 flex items-center justify-between border border-white/25 h-14 px-6 rounded-full font-mono hover:border-brass hover:text-brass transition-colors">
                <span className="flex items-center gap-2"><Phone className="h-4 w-4" />Call</span>{SITE.phone}
              </a>
              {slug === "blocked-drains" && (
                <Link to="/24-hour-service" data-testid="service-247-link" className="mt-6 flex items-center gap-3 text-sm text-slate-300 hover:text-brass transition-colors">
                  <Siren className="h-4 w-4 text-brass" /> Blocked right now? Our 24/7 team is available day and night.
                </Link>
              )}
            </div>
          </aside>
        </div>
      </section>
      <Process dark />
      <section className="bg-bone py-20 sm:py-24">
        <div className="container-x">
          <p className="eyebrow text-cobalt mb-8">More services</p>
          <div className="grid gap-4 md:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`related-${o.slug}`} className="group relative block aspect-[4/3] overflow-hidden bg-ink clip-notch-sm">
                <img src={o.image} alt={o.title} className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-1000" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                <p className="absolute bottom-6 left-6 font-wide font-black uppercase text-2xl text-white">{o.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
